import React from 'react'
import {
  Column,
  ColumnDef,
  ColumnFiltersState,
  RowData,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { Button ,Card ,Col, Row, Table } from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';
import "./style.css"

declare module '@tanstack/react-table' {
  interface ColumnMeta<TData extends RowData, TValue> {
    filterVariant?: 'text' | 'range' | 'select'
    bodyTextAlign?: 'left' | 'right' | 'center' | 'justify' | undefined;
    maxWidth?: string; 
  }
}

export type CustomColumnDef<T> = ColumnDef<T, any> & {
  hasCheckbox?: boolean; // カスタムプロパティを追加  
}

type FilteringListProp<T> = {
    style?: React.CSSProperties | undefined
    className?: string | undefined
    sysStatus?: number | undefined
    columns: CustomColumnDef<T>[]
    datas: T[],
    onCheck?: (selected: T[]) => void
    onClick?: (clicked: T) => void
}

const isNotEmpty = (obj:{}) => {
  return Object.keys(obj).length !== 0
}

export const FilteringList = <T,>(props:FilteringListProp<T>) => {
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    []
  );

  const columns = props.columns;

  const [data, setData] = React.useState<T[]>(props.datas);
  const [rowSelection, setRowSelection] = React.useState({});
  const [prevRowSelection, setPrevRowSelection] = React.useState(rowSelection);

  React.useEffect(() => {
    setData(props.datas);
  }, [props.datas]);

  const table = useReactTable({
    data,
    columns,
    filterFns: {},
    state: {
      columnFilters,
      rowSelection,
    },
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onRowSelectionChange: setRowSelection,
  });

  if(props.onCheck && isNotEmpty(rowSelection) && JSON.stringify(rowSelection) !== JSON.stringify(prevRowSelection)) {
    const selectedRecord:T[] = [];
    Object.keys(rowSelection).map((key:string, index:number) => {
      selectedRecord.push(props.datas[Number(key)]);
    });
    props.onCheck(selectedRecord);
    setPrevRowSelection(rowSelection);
  }

  return (
    <div className='mx-auto my-3' style={{width:"98%"}}>
      <Card className='shadow my-3' style={{ overflowX: 'auto' }}>
        <Table className='filtering-list-wrapper'>
          <thead className="double-underline">
            {table.getHeaderGroups().map(headerGroup => (              
              <tr key={headerGroup.id}>
                {headerGroup.headers.map(header => {
                  const meta = header.column.columnDef.meta || {};
                    return (
                    <th key={header.id} colSpan={header.colSpan} className="text-center px-3" style={{ maxWidth: meta.maxWidth, whiteSpace: "nowrap" }}>
                        {header.isPlaceholder ? null : (
                        <>
                            <div
                                {...{
                                    className: header.column.getCanSort()
                                    ? 'cursor-pointer select-none'
                                    : '',
                                    onClick: header.column.getToggleSortingHandler(),
                                }}
                            >
                                {flexRender(
                                    header.column.columnDef.header,
                                    header.getContext()
                                )}
                                {{
                                    asc: ' 🔼',
                                    desc: ' 🔽',
                                }[header.column.getIsSorted() as string] ?? null}
                            </div>
                            {header.column.getCanFilter() ? (
                            <div style={{width: "fit-content", margin: "0 auto"}}>
                                <Filter column={header.column} />
                            </div>
                            ) : null}
                        </>
                        )}
                    </th>
                    )
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map(row => {
                return (
                <tr 
                  key={row.id}
                >
                    {row.getVisibleCells().map(cell => {
                      const meta = cell.column.columnDef.meta || {};
                    return (
                        <td 
                          key={cell.id} 
                          className="text-center px-3 align-middle text-nowrap"
                          style={{ 
                            textAlign: meta.bodyTextAlign,
                            maxWidth: meta.maxWidth,
                            cursor: (window.location.pathname === '/partners' || props.sysStatus === 0 || props.sysStatus === 1) ? 'pointer' : 'default'
                          }}
                          onClick={()=>{
                            if(!((cell.column.columnDef) as CustomColumnDef<T>).hasCheckbox && props.onClick) {
                              props.onClick(props.datas[row.index]);                            
                            }
                          }}
                        >
                            {flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext()
                            )}
                        </td>
                    )
                    })}
                </tr>
                )
            })}
          </tbody>
        </Table>
      </Card>
        <div className="h-2" />
        <div style={{display: "flex", justifyContent: "center"}} className="flex items-center gap-2">
            <Button
                onClick={() => table.setPageIndex(0)}
                disabled={!table.getCanPreviousPage()}
                variant="dark"
                size='lg'
            >
                {'<<'}
            </Button>
            <Button
                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage()}
                variant="dark"
            >
            {'<'}
            </Button>
            <Button
                onClick={() => table.nextPage()}
                disabled={!table.getCanNextPage()}
                variant="dark"
            >
            {'>'}
            </Button>
            <Button
                onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                disabled={!table.getCanNextPage()}
                variant="dark"
            >
            {'>>'}
            </Button>
        </div>
        <div className='my-3' style={{display: "flex", justifyContent: "center"}}>
            全{table.getRowCount()}件中{'　'}
            {
                table.getState().pagination.pageIndex*table.getState().pagination.pageSize+1}
                    - 
                {table.getState().pagination.pageSize+table.getState().pagination.pageIndex*table.getState().pagination.pageSize
            }件を表示中
        </div>
    </div>
  );
}

function Filter({ column }: { column: Column<any, unknown> }) {
  const columnFilterValue = column.getFilterValue()
  const { filterVariant } = column.columnDef.meta ?? {}

  return filterVariant === 'range' ? (
    <div>
      <div className="space-x-2">
        {/* See faceted column filters example for min max values functionality */}
        <Row className='flex-nowrap'>
          <Col className='p-0'>
            <DebouncedInput
              type="number"
              value={(columnFilterValue as [number, number])?.[0] ?? ''}
              onChange={value =>
                column.setFilterValue((old: [number, number]) => [value, old?.[1]])
              }
              placeholder={`Min`}
              className="border shadow rounded"
              style={{width:"50px"}}
            />
          </Col>
          <Col className='p-0'>
            <DebouncedInput
              type="number"
              value={(columnFilterValue as [number, number])?.[1] ?? ''}
              onChange={value =>
                column.setFilterValue((old: [number, number]) => [old?.[0], value])
              }
              placeholder={`Max`}
              className="flex-nowrap border shadow rounded"
              style={{width:"50px"}}
            />
          </Col>
        </Row>
      </div>
    </div>
  ) : filterVariant === 'select' ? (
    <select
      onChange={e => column.setFilterValue(e.target.value)}
      value={columnFilterValue?.toString()}
    >
      {/* See faceted column filters example for dynamic select options */}
      <option value="">All</option>
      <option value="complicated">complicated</option>
      <option value="relationship">relationship</option>
      <option value="single">single</option>
    </select>
  ) : (
    <DebouncedInput
      className="w-36 border shadow rounded"
      onChange={value => column.setFilterValue(value)}
      placeholder={`Search...`}
      type="text"
      style={{width:"100%", minWidth:"100px"}}
      value={(columnFilterValue ?? '') as string}
    />
    // See faceted column filters example for datalist search suggestions
  )
}

function DebouncedInput({
  value: initialValue,
  onChange,
  debounce = 500,
  ...props
}: {
  value: string | number
  onChange: (value: string | number) => void
  debounce?: number
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'>) {
  const [value, setValue] = React.useState(initialValue)

  React.useEffect(() => {
    setValue(initialValue)
  }, [initialValue])

  React.useEffect(() => {
    const timeout = setTimeout(() => {
      onChange(value)
    }, debounce)

    return () => clearTimeout(timeout)
  }, [value])

  return (
    <input {...props} value={value} onChange={e => setValue(e.target.value)} />
  )
}


